import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, RefreshCw, CheckCircle2, Building2, User, Trophy, MapPin } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";

const COUNTRY_FLAGS = {
  IT: "🇮🇹", FR: "🇫🇷", ES: "🇪🇸", DE: "🇩🇪", UK: "🇬🇧", PT: "🇵🇹",
  NL: "🇳🇱", BE: "🇧🇪", PL: "🇵🇱", SE: "🇸🇪", NO: "🇳🇴", FI: "🇫🇮",
  DK: "🇩🇰", CZ: "🇨🇿", GR: "🇬🇷", CH: "🇨🇭", GBZ: "🇬🇮", IRL: "🇮🇪",
};
const COUNTRY_NAMES = {
  IT: "Italia", FR: "Francia", ES: "Spagna", DE: "Germania", UK: "Regno Unito",
  PT: "Portogallo", NL: "Olanda", BE: "Belgio", PL: "Polonia", SE: "Svezia",
  NO: "Norvegia", FI: "Finlandia", DK: "Danimarca", CZ: "Rep. Ceca",
  GR: "Grecia", CH: "Svizzera", GBZ: "Gibilterra", IRL: "Irlanda",
};

const flag = (c) => COUNTRY_FLAGS[c] || "🏳";
const name = (c) => COUNTRY_NAMES[c] || c;

const SourceBadge = ({ source }) => (
  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
    source === 'vat'
      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
      : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
  }`}>
    {source === 'vat' ? 'P.IVA' : 'Telefono'}
  </span>
);

export const MismatchDetectionWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(null);          // customer.id while POSTing
  const [confirmId, setConfirmId] = useState(null);    // customer.id for confirm modal
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/dashboard/mismatch-detection?days=30`);
      setData(r.data);
    } catch (e) {
      console.error("Mismatch load failed", e);
      toast.error("Errore nel caricamento dei mismatch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); const t = setInterval(load, 120_000); return () => clearInterval(t); }, []);

  const applyFix = async (cust) => {
    try {
      setFixing(cust.id);
      await axios.post(`${API}/customers/${cust.id}/fix-country`, {
        new_country: cust.suggested_country,
        reason: reason || "Corretto dal widget Mismatch Detection (admin)",
      });
      toast.success(`Paese aggiornato: ${flag(cust.suggested_country)} ${name(cust.suggested_country)}`);
      setConfirmId(null);
      setReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore correzione");
    } finally {
      setFixing(null);
    }
  };

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="mismatch-widget">
        {loading
          ? <div className="spinner mx-auto" />
          : <p className="text-gray-500 text-sm">Nessun dato</p>}
      </div>
    );
  }

  const isClean = data.mismatched_count === 0;

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="mismatch-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <MapPin size={18} className="text-lojack-clause-400" />
            Mismatch Paese Clienti
          </h3>
          <p className="text-gray-500 text-xs mt-1">Ultimi {data.window_days} giorni — copertura intera rete LoJack</p>
        </div>
        <button onClick={load} className="text-gray-500 hover:text-white transition" data-testid="mismatch-refresh">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-black/40 rounded-xl p-3 border border-gray-800">
          <div className="text-2xl font-bold text-white" data-testid="mismatch-total">{data.total_in_window}</div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Clienti creati</div>
        </div>
        <div className={`bg-black/40 rounded-xl p-3 border ${isClean ? 'border-lojack-blue-500/30' : 'border-lojack-clause-500/30'}`}>
          <div className={`text-2xl font-bold ${isClean ? 'text-lojack-blue-400' : 'text-lojack-clause-400'}`} data-testid="mismatch-count">
            {data.mismatched_count}
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Mismatch rilevati</div>
        </div>
        <div className="bg-black/40 rounded-xl p-3 border border-gray-800">
          <div className={`text-2xl font-bold ${data.mismatch_rate_pct <= 5 ? 'text-lojack-blue-400' : data.mismatch_rate_pct <= 15 ? 'text-lojack-clause-400' : 'text-red-400'}`}>
            {data.mismatch_rate_pct.toFixed(1)}%
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">Tasso mismatch</div>
        </div>
      </div>

      {isClean && (
        <div className="bg-lojack-blue-500/10 border border-lojack-blue-500/30 rounded-xl p-4 text-center" data-testid="mismatch-clean">
          <CheckCircle2 className="text-lojack-blue-400 mx-auto mb-1" size={24} />
          <p className="text-lojack-blue-300 text-sm font-semibold">Nessun mismatch rilevato 🎉</p>
          <p className="text-gray-500 text-xs mt-1">I dealer stanno usando bene il banner paese.</p>
        </div>
      )}

      {!isClean && (
        <>
          {/* Dealer leaderboard */}
          {data.dealer_leaderboard.length > 0 && (
            <div className="mb-4">
              <h4 className="text-gray-400 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Trophy size={12} /> Top dealer con mismatch
              </h4>
              <div className="space-y-1.5">
                {data.dealer_leaderboard.slice(0, 5).map((d, i) => (
                  <div key={d.dealer_code} className="flex items-center justify-between bg-black/30 px-3 py-2 rounded-lg border border-gray-800/50">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-500 text-xs w-4">{i + 1}.</span>
                      <span className="text-white text-sm truncate">{d.dealer_name}</span>
                      <span className="text-gray-600 text-[10px]">{d.dealer_code}</span>
                    </div>
                    <span className="text-lojack-clause-400 text-sm font-bold tabular-nums">{d.mismatched_count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mismatched customers list */}
          <div>
            <h4 className="text-gray-400 text-xs uppercase tracking-wider mb-2">Clienti da rivedere</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {data.mismatched_customers.map(c => (
                <div key={c.id} className="bg-black/30 rounded-lg border border-gray-800 p-3" data-testid={`mismatch-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {c.customer_type === 'business'
                          ? <Building2 size={12} className="text-purple-400 flex-shrink-0" />
                          : <User size={12} className="text-blue-400 flex-shrink-0" />}
                        <span className="text-white text-sm font-medium truncate">{c.display_name}</span>
                        <SourceBadge source={c.source} />
                      </div>
                      <div className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span>{c.email}</span>
                        {c.dealer_code && <span className="text-gray-600">· {c.dealer_code}</span>}
                      </div>
                      <div className="text-xs text-gray-400 mt-1.5">
                        <span className="text-gray-500">Attuale:</span> {flag(c.current_country)} {name(c.current_country)}
                        <span className="mx-2 text-gray-600">→</span>
                        <span className="text-lojack-clause-400 font-medium">{flag(c.suggested_country)} {name(c.suggested_country)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setConfirmId(c.id); setReason(""); }}
                      disabled={fixing === c.id}
                      className="px-3 py-1.5 bg-lojack-clause-600 hover:bg-lojack-clause-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-xs font-medium transition flex-shrink-0"
                      data-testid={`mismatch-fix-${c.id}`}>
                      Correggi
                    </button>
                  </div>

                  {confirmId === c.id && (
                    <div className="mt-3 pt-3 border-t border-gray-800 space-y-2" data-testid={`mismatch-confirm-${c.id}`}>
                      <p className="text-xs text-gray-300">
                        Conferma cambio paese a <span className="text-lojack-clause-400 font-medium">{flag(c.suggested_country)} {name(c.suggested_country)}</span>?
                      </p>
                      <input
                        type="text"
                        placeholder="Motivo correzione (opzionale — audit log)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="w-full bg-black border border-gray-700 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-lojack-clause-500 outline-none"
                        data-testid={`mismatch-reason-${c.id}`}
                        maxLength={500}
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => applyFix(c)}
                          disabled={fixing === c.id}
                          className="flex-1 px-3 py-1.5 bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 rounded-lg text-white text-xs font-medium"
                          data-testid={`mismatch-confirm-apply-${c.id}`}>
                          {fixing === c.id ? "Salvataggio..." : "Conferma correzione"}
                        </button>
                        <button
                          onClick={() => { setConfirmId(null); setReason(""); }}
                          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xs">
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MismatchDetectionWidget;
